/**
 * LAGOSDashboard — State Summary Dashboard — Upgraded
 * /state/lagos/dashboard
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useLagos } from "@/context/LAGOSContext";
import { SectionCard } from "@/components/ds";
import { Building2, Leaf, TrendingUp, Award, MapPin, BarChart2, Users, Zap, ArrowRight } from "lucide-react";

const fmtN = (v, u="") => { if (!v && v!==0) return "—"; const n=Number(v); if (n>=1e6) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n>=1000) return `${(n/1000).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };
const fmtU = (v) => { if (!v) return "—"; const n=Number(v); if (n>=1e9) return `$${(n/1e9).toFixed(1)}B`; if (n>=1e6) return `$${(n/1e6).toFixed(1)}M`; if (n>=1000) return `$${(n/1000).toFixed(0)}K`; return `$${n}`; };

const LABEL_COLORS = { Platinum:"#7c3aed", Gold:"#d97706", Silver:"#64748b", Bronze:"#b45309", Emerging:"#94a3b8" };
const LABEL_BG     = { Platinum:"#f5f3ff", Gold:"#fffbeb",  Silver:"#f8fafc", Bronze:"#fef3c7", Emerging:"#f8fafc" };

const KPI = ({ icon:Icon, label, value, sub, color="#059669" }) => (
  <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex items-start gap-3">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{background:color+"18"}}>
      <Icon size={16} style={{color}} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-xl font-black text-slate-900 mt-0.5 leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

export default function LAGOSDashboard() {
  const { portfolio, loading, periods, selectedPeriod, setSelectedPeriod, cpaPoaData } = useLagos();
  const navigate = useNavigate();

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading state data…</div>;
  const labels = portfolio.label_distribution || {};
  const poa = cpaPoaData?.poa_summary || {};

  return (
    <div className="p-6 lg:p-8 space-y-5 bg-[#F8FAFC] min-h-screen">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded">Lagos State Government</span>
            <span className="text-xs text-slate-400">Climate, Circularity & Carbon Platform</span>
          </div>
          <h1 className="text-2xl font-black text-slate-900">State Climate Intelligence Dashboard</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            {fmtN(portfolio.total_entities)} entities · {Object.keys(portfolio.by_lga||{}).length} LGAs · {Object.keys(portfolio.by_sector||{}).length} sectors
          </p>
        </div>
        <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 bg-white" data-testid="period-selector">
          {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
        </select>
      </div>

      {/* Executive Summary Bar */}
      <div className="bg-[#0f172a] rounded-xl p-5 text-white">
        <p className="text-[9px] font-bold uppercase tracking-widest opacity-40 mb-3">State Portfolio — Executive Summary</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          {[
            ["GHG Baseline",        fmtN(portfolio.total_tco2e_yr,"tCO₂e/yr"),        "#f87171"],
            ["Reduction Potential", fmtN(portfolio.reduction_potential_tco2e,"tCO₂e/yr"), "#34d399"],
            ["Carbon Value (Base)", fmtU(portfolio.carbon_value_base_usd)+"/yr",       "#fbbf24"],
            ["Entities Monitored",  String(portfolio.total_entities||0),               "#94a3b8"],
          ].map(([k,v,c])=>(
            <div key={k}>
              <p className="text-[9px] opacity-40 uppercase tracking-wider">{k}</p>
              <p className="text-xl font-black mt-0.5" style={{color:c}}>{v}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 pt-3 border-t border-white/10">
          <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded">{portfolio.vcm_eligible_count||0} VCM eligible</span>
          <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded">{portfolio.art6_eligible_count||0} Art.6.4 conditional</span>
          <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded">{poa.eligible_cpas||0} CPAs PoA-eligible</span>
          <span className="text-[10px] bg-white/10 px-2 py-0.5 rounded">Carbon value indicative — NOT in base DSCR</span>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Building2} label="Entities Onboarded"   value={fmtN(portfolio.total_entities)} sub="across 10 LGAs" color="#1d4ed8" />
        <KPI icon={Award}     label="VCM Eligible"         value={fmtN(portfolio.vcm_eligible_count)} sub="with VCM pathway" color="#7c3aed" />
        <KPI icon={Users}     label="Art.6.4 Eligible"     value={fmtN(portfolio.art6_eligible_count)} sub="conditional — NCCC req'd" color="#0891b2" />
        <KPI icon={BarChart2} label="Avg Climate Score"    value={(portfolio.climate_score_avg||0).toFixed(1)+"/100"} sub="state avg" color="#059669" />
        <KPI icon={Leaf}      label="Avg Circularity"      value={(portfolio.circularity_score_avg||0).toFixed(1)+"/100"} sub="state avg" color="#d97706" />
        <KPI icon={TrendingUp}label="Avg Readiness Score"  value={(portfolio.readiness_score_avg||0).toFixed(1)+"/100"} sub="DMRV readiness" color="#0f172a" />
        <KPI icon={MapPin}    label="LGAs Active"          value={Object.keys(portfolio.by_lga||{}).length} sub={Object.keys(portfolio.by_lga||{}).slice(0,2).join(", ")+"..."} color="#64748b" />
        <KPI icon={Zap}       label="Recycle Credit"       value={fmtN(portfolio.recycle_credit_tco2e,"tCO₂e/yr")} sub="circularity offset" color="#059669" />
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Eco Label Distribution — visual */}
        <SectionCard title="Eco Label Distribution">
          <div className="p-4 space-y-3">
            {["Platinum","Gold","Silver","Bronze","Emerging"].map(lbl => {
              const count = labels[lbl] || 0;
              const pct = portfolio.total_entities ? (count/portfolio.total_entities*100) : 0;
              return (
                <div key={lbl}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="font-bold" style={{color: LABEL_COLORS[lbl]}}>{lbl}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 text-[10px]">{pct.toFixed(0)}%</span>
                      <span className="font-black text-slate-700 w-5 text-right">{count}</span>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{width:pct+"%", background: LABEL_COLORS[lbl]}} />
                  </div>
                </div>
              );
            })}
            <button onClick={()=>navigate("/state/lagos/eco-labels")}
              className="w-full mt-1 text-[10px] text-emerald-600 hover:text-emerald-700 flex items-center justify-center gap-1 pt-1"
              data-testid="view-eco-labels">
              View all labels <ArrowRight size={10} />
            </button>
          </div>
        </SectionCard>

        {/* Top LGAs */}
        <SectionCard title="Top LGAs — Reduction Potential">
          <div className="p-4">
            {Object.entries(portfolio.by_lga||{}).sort((a,b)=>b[1].reduction-a[1].reduction).slice(0,5).map(([lga,d], i) => {
              const max = Math.max(...Object.values(portfolio.by_lga||{}).map(x=>x.reduction||0), 1);
              return (
                <div key={lga} className="space-y-0.5 py-2 border-b border-slate-50 last:border-0">
                  <div className="flex justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-black text-slate-300 w-3">{i+1}</span>
                      <span className="font-medium text-slate-700">{lga}</span>
                    </div>
                    <span className="font-black text-emerald-700 text-xs">{fmtN(d.reduction,"tCO₂e")}</span>
                  </div>
                  <div className="w-full h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-emerald-500" style={{width:`${(d.reduction/max)*100}%`}} />
                  </div>
                  <p className="text-[9px] text-slate-400">{d.count} entities</p>
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* PoA Summary */}
        <SectionCard title="National PoA Status">
          <div className="p-4 space-y-3">
            <div className="bg-[#0f172a] rounded-xl p-3 text-white text-center">
              <p className="text-[9px] opacity-40 uppercase tracking-wider mb-1">State PoA Coverage</p>
              <p className="text-2xl font-black text-emerald-400">{fmtN(poa.total_reduction||0,"tCO₂e/yr")}</p>
              <p className="text-[10px] opacity-40">{poa.eligible_cpas||0} CPAs · {poa.total_entities||0} entities</p>
            </div>
            {[
              ["Carbon Value (Base)", fmtU(poa.carbon_value_base_usd)],
              ["CPAs PoA-eligible",   String(poa.eligible_cpas||0)],
              ["Status",              poa.status||"Design Phase"],
            ].map(([k,v])=>(
              <div key={k} className="flex justify-between text-xs py-1 border-b border-slate-50">
                <span className="text-slate-500">{k}</span><span className="font-bold">{v}</span>
              </div>
            ))}
            <button onClick={()=>navigate("/state/lagos/carbon-structuring")}
              className="w-full mt-1 bg-blue-50 text-blue-600 text-xs py-2 rounded-lg hover:bg-blue-100 flex items-center justify-center gap-1 font-medium"
              data-testid="view-structuring">
              View Carbon Structuring <ArrowRight size={10} />
            </button>
          </div>
        </SectionCard>
      </div>

      {/* Top Opportunities */}
      {portfolio.top_opportunities?.length > 0 && (
        <SectionCard title="Top 10 Opportunities (by Composite Score)">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {["#","Entity","Sector","LGA","Reduction","Carbon (Base)","Pathway","Label","Score"].map(h=>(
                    <th key={h} className="text-left py-2.5 px-3 text-slate-500 font-semibold text-[11px]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {portfolio.top_opportunities.slice(0,8).map((o,i)=>(
                  <tr key={o.entity_id} className="border-b border-slate-50 hover:bg-blue-50/30 cursor-pointer transition-colors"
                    onClick={()=>navigate(`/state/lagos/entity/${o.entity_id}`)}>
                    <td className="py-2.5 px-3 text-slate-400 font-bold">{i+1}</td>
                    <td className="py-2.5 px-3 font-semibold max-w-[160px] truncate">{o.name}</td>
                    <td className="py-2.5 px-3 text-slate-500 capitalize">{o.sector?.replace(/_/g," ")}</td>
                    <td className="py-2.5 px-3 text-slate-500">{o.lga}</td>
                    <td className="py-2.5 px-3 text-emerald-700 font-black">{fmtN(o.reduction_potential_tco2e,"tCO₂e")}</td>
                    <td className="py-2.5 px-3 text-blue-700 font-bold">{fmtU(Number(o.reduction_potential_tco2e||0)*20)}/yr</td>
                    <td className="py-2.5 px-3">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-50 text-blue-700 capitalize">{o.carbon_pathway}</span>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-[10px] font-black px-2 py-0.5 rounded-full" style={{background:LABEL_BG[o.eco_label]||"#f8fafc",color:LABEL_COLORS[o.eco_label]||"#94a3b8"}}>{o.eco_label}</span>
                    </td>
                    <td className="py-2.5 px-3 text-right font-black text-slate-800">{(o.composite_score||0).toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}
